import { Badge } from '@civilgrid/ui/components';
import { isEmpty } from 'lodash';
import type React from 'react';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { IntelligenceSectionTitle, IntelligenceStatus, NO_CONFLICTS, PENDING } from '../common';
import { nearbyProjectsLayerIds } from '../common/config';
import { getRollupCount } from '../common/getRollupCount';
import IntelligenceSection from '../common/IntelligenceSection';
import Table from '../common/Table';
import Toggle from '../common/Toggle';
import store from '../store';

const PGE = 'pacific gas & electric company';

const getCount = (agencyIds: string[]): React.ReactNode => {
  let count = 0;

  const allPublished = agencyIds.every((id) => {
    const a = store.citygrid[id];
    if (a.properties?.published === 1) {
      return true;
    }

    return false;
  });

  nearbyProjectsLayerIds.forEach((layerId) => {
    const features = store.config[layerId];

    if (features) {
      Object.keys(features)?.forEach((featureId) => {
        const f = features[featureId];
        if (f?.properties?.civilgrid_id) {
          if (agencyIds.includes(f.properties.civilgrid_id)) {
            count += 1;
          }
        }
      });
    }
  });

  if (allPublished === true && count === 0) {
    return NO_CONFLICTS;
  }

  if (!allPublished) {
    return PENDING;
  }

  return count;
};

export default function NearbyProjectsComponent() {
  const { citygrid, config, projectIntelligence } = useSnapshot(store) as typeof store;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const agencies = useMemo(() => {
    const els: JSX.Element[] = [];

    const agencyMap: { [key: string]: string[] } = {};

    Object.keys(citygrid).forEach((agencyId) => {
      const f = citygrid[agencyId];

      if (f) {
        let agencyname = f.properties?.agencyname?.trim();

        if (agencyname) {
          // temp - hard code "Gas Projects" in name of PG&E;
          if (agencyname.toLowerCase() === PGE) {
            agencyname = `${agencyname} - Gas Projects`;
          }

          if (!agencyMap[agencyname]) {
            agencyMap[agencyname] = [];
          }

          agencyMap[agencyname].push(agencyId);
        }
      }
    });

    Object.keys(agencyMap)?.forEach((agencyname, i) => {
      const tables: JSX.Element[] = [];

      const agencyIds = agencyMap[agencyname];

      agencyIds.forEach((agencyId, index) => {
        const agency = citygrid[agencyId];
        if (agency?.properties?.type) {
          let tableProps: any = {
            agencyId,
            size: 'small',
            sortBy: 'endDate',
            columns: ['Overlap (sq ft)', 'Year Start', 'Year End', 'More'],
            moreInfo: {
              columns: [
                'Agency',
                'Overlap (sq ft)',
                'Year Start',
                'Year End',
                'Last Updated',
                'Forecasted',
                'PDF',
                'Link',
                'Contact Name',
                'Contact Email',
                'Notes',
              ],
              columnProperties: [
                'owner',
                'projectOverlap',
                'startDate',
                'endDate',
                'load_date',
                'forecasted',
                'pdf_link',
                'agency_link',
                'outreach_email_name',
                'outreach_email',
                'notes',
              ],
            },
          };

          // if (agency.properties?.type === 'Projects') {
          //   tableProps = {
          //     ...tableProps,
          //     emptyMessage:
          //       'No known planned projects for this agency/jurisdiction at this location',
          //     label: 'Planned Projects',
          //     filter: [{ property: 'civilgrid_id', value: agencyId }],
          //     rows: [
          //       {
          //         layerId: 'projects',
          //         columnProperties: ['projectOverlap', 'startDate', 'endDate'],
          //       },
          //     ],
          //   };
          // } else
          if (agency.properties?.type === 'Paving Projects') {
            tableProps = {
              ...tableProps,
              emptyMessage: 'No known paving projects for this agency/jurisdiction at this location',
              label: 'Paving Projects',
              filter: [{ property: 'civilgrid_id', value: agencyId }],
              order: 1,
              rows: [
                {
                  layerId: 'pavingProjects',
                  columnProperties: ['projectOverlap', 'startDate', 'endDate'],
                },
              ],
            };
          } else if (agency.properties?.type === 'Moratoriums') {
            tableProps = {
              ...tableProps,
              emptyMessage: 'No known paving moratoria for this agency/jurisdiction',
              label: 'Paving Moratoria',
              order: 2,
              filter: [{ property: 'civilgrid_id', value: agencyId }],
              rows: [
                {
                  layerId: 'pavingMoratorium',
                  columnProperties: ['projectOverlap', 'startDate', 'endDate'],
                },
              ],
            };
          } else if (agency.properties?.type === 'CIP') {
            tableProps = {
              ...tableProps,
              emptyMessage: 'No known capital improvement projects for this agency/jurisdiction at this location',
              label: 'Capital Improvement Projects',
              filter: [{ property: 'civilgrid_id', value: agencyId }],
              order: 0,
              rows: [
                {
                  layerId: 'cipProjects',
                  columnProperties: ['projectOverlap', 'startDate', 'endDate'],
                },
              ],
            };
          }

          if (tableProps.label) {
            tables.push(
              <Table
                // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                key={`${agencyname}-project-table-${index}`}
                {...tableProps}
                defaultOpen={i === 0}
              />,
            );
          }
        }
      });

      const t = (
        <Toggle
          featureCount={getCount(agencyIds)}
          // biome-ignore lint/suspicious/noArrayIndexKey: legacy
          key={`nearby-projects-${i}-${agencyname}`}
          label={agencyname}
          defaultOpen={i === 0}
          agencyIds={agencyIds}
        >
          {tables.sort((a, b) => a.props.order - b.props.order)}
        </Toggle>
      );

      els.push(t);
    });

    if (isEmpty(els)) {
      return <div className="px-2 text-xs">No known agencies/jurisdictions in the project area.</div>;
    }

    return els;
  }, [citygrid, config]);

  const overlapCount = useMemo(() => getRollupCount(config, nearbyProjectsLayerIds), [config]);

  const needsAttnCount = useMemo(() => {
    let count = 0;

    Object.keys(projectIntelligence).forEach((id) => {
      const item = projectIntelligence[id];

      if (item) {
        if (nearbyProjectsLayerIds.includes(item.layerId) && item.reviewStatus === IntelligenceStatus.NeedsAttention) {
          count += 1;
        }
      }
    });

    return count;
  }, [projectIntelligence]);

  return (
    <IntelligenceSection
      title={IntelligenceSectionTitle.NearbyProjects}
      featureCount={overlapCount}
      subheader={
        <>
          {overlapCount > 0 && <Badge color="orange">Overlaps: {overlapCount}</Badge>}
          {needsAttnCount > 0 && <Badge color="red">Needs Attention: {needsAttnCount}</Badge>}
        </>
      }
    >
      {agencies}
    </IntelligenceSection>
  );
}
